import { Show, For, createMemo, createSignal } from 'solid-js';
import useWidgetData from './useWidgetData.js';
import { RecentErrorsPanel } from './RecentErrors.jsx';
import { ServerPerfPanel } from './ServerPerf.jsx';
import { StorageCleanupPanel } from './StorageCleanup.jsx';
import { SprintRunnerPanel } from './SprintRunner.jsx';
import { FissalPrimePanel } from './FissalPrime.jsx';
import AdminTools from './AdminTools.jsx';
import wm from '../lib/wmStore.js';

const PANEL_DEFS = [
  { key: 'errors', label: 'Errors', icon: '⛔', summary: 'Failed requests and captured exceptions.' },
  { key: 'performance', label: 'Performance', icon: '⚡', summary: 'Process, latency, cache, sessions, routes, diagnostics.' },
  { key: 'cleanup', label: 'Cleanup', icon: '🗄️', summary: 'Chart cache usage and cleanup controls.' },
  { key: 'sprint-runner', label: 'Issue Backlog', icon: '🛰️', summary: 'Owner-only live GitHub backlog and model-tier recommendations.' },
  { key: 'fissal-prime', label: 'Fissal Prime', icon: '🜂', summary: 'Owner-only privileged tier: model routing, failover, proposals, sweep.' },
  { key: 'controls', label: 'Controls / PM2', icon: '🛠️', summary: 'Owner-only process status, restart and hot-reload controls.' },
];

function panelSettings(props, key) {
  const nested = props.settings?.panels?.[key];
  if (nested && typeof nested === 'object') return nested;
  return props.settings || {};
}

function OwnerGate() {
  return (
    <div class="v2-system__gate">
      <div class="v2-system__gate-title">Owner access required</div>
      <p>This panel keeps its original owner-only permission boundary. Admin operators can use the other System panels from this window.</p>
    </div>
  );
}

export default function System(props) {
  const typeToPanel = {
    'recent-errors': 'errors',
    'server-perf': 'performance',
    'storage-cleanup': 'cleanup',
    'sprint-runner': 'sprint-runner',
    'fissal-prime': 'fissal-prime',
    'admin-tools': 'controls',
  };
  const initial = PANEL_DEFS.some(p => p.key === props.settings?.panel)
    ? props.settings.panel
    : (typeToPanel[props.widget?.type] || 'errors');
  const [activePanel, setActivePanelRaw] = createSignal(initial);
  function setActivePanel(key) {
    setActivePanelRaw(key);
    const wid = props.widget?.id;
    if (wid) {
      wm.setSettings(wid, { ...(props.widget?.settings || {}), panel: key });
    }
  }
  const { data, loading, error } = useWidgetData('system', () => ({ panel: activePanel() }));
  const viewerIsOwner = () => !!props.identity?.isOwner;
  const panelMeta = createMemo(() => {
    const byKey = new Map((data()?.panels || []).map(panel => [panel.key, panel]));
    return PANEL_DEFS.map(panel => ({
      ...panel,
      available: byKey.get(panel.key)?.available !== false,
    }));
  });

  return (
    <div class="v2-widget v2-system">
      <div class="v2-system__topline">
        <div>
          <div class="v2-system__eyebrow">Admin operations</div>
          <div class="v2-system__title">System</div>
        </div>
        <Show when={error()}>
          <span class="v2-system__error">{error()}</span>
        </Show>
      </div>

      <div class="v2-system__tabs" role="tablist" aria-label="System panels" aria-busy={loading() ? 'true' : 'false'}>
        <For each={panelMeta()}>{panel => (
          <button
            type="button"
            role="tab"
            class="v2-system__tab"
            classList={{ 'is-active': activePanel() === panel.key, 'is-locked': !panel.available }}
            aria-selected={activePanel() === panel.key}
            onClick={() => setActivePanel(panel.key)}
            title={panel.summary}
          >
            <span class="v2-system__tab-icon">{panel.icon}</span>
            <span>{panel.label}</span>
          </button>
        )}</For>
      </div>

      <div class="v2-system__body">
        <Show when={activePanel() === 'errors'}>
          <RecentErrorsPanel widget={props.widget} settings={panelSettings(props, 'errors')} />
        </Show>
        <Show when={activePanel() === 'performance'}>
          <ServerPerfPanel widget={props.widget} settings={panelSettings(props, 'performance')} />
        </Show>
        <Show when={activePanel() === 'cleanup'}>
          <StorageCleanupPanel widget={props.widget} settings={panelSettings(props, 'cleanup')} />
        </Show>
        <Show when={activePanel() === 'sprint-runner'}>
          <Show when={viewerIsOwner()} fallback={<OwnerGate />}>
            <SprintRunnerPanel widget={props.widget} settings={panelSettings(props, 'sprint-runner')} />
          </Show>
        </Show>
        <Show when={activePanel() === 'fissal-prime'}>
          <Show when={viewerIsOwner()} fallback={<OwnerGate />}>
            <FissalPrimePanel widget={props.widget} settings={panelSettings(props, 'fissal-prime')} />
          </Show>
        </Show>
        <Show when={activePanel() === 'controls'}>
          <Show when={viewerIsOwner()} fallback={<OwnerGate />}>
            <AdminTools widget={props.widget} settings={panelSettings(props, 'controls')} />
          </Show>
        </Show>
      </div>
    </div>
  );
}